/**
 * GuildPluginSharingCard — the owner's control for whether PLUGIN arches may
 * create shared workspaces for this guild (Settings tab of Guild Management).
 *
 * Deliberately a separate control from GuildDataSpaceCard, not a second line
 * in it. That card governs the guild's Vault space: platform-curated document
 * and note storage. This one exposes the guild to THIRD-PARTY plugin code —
 * it is the only channel through which a plugin can learn the guild exists,
 * and it lets a plugin claim a shared data space inside its own storage. An
 * owner who wanted shared documents has not thereby agreed to that, so it
 * takes its own decision and its own confirmation.
 *
 * Unlike the Vault space this IS reversible: plugin data lives in each
 * arche's own store rather than in member-visible platform surfaces, so
 * turning it off strands nothing — it makes the guild ineligible immediately
 * and the data becomes unreachable until it is turned back on.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { Blocks } from 'lucide-react';
import { api, type GuildDetailResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function GuildPluginSharingCard({ guild }: { guild: GuildDetailResponse }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const isOwner = guild.myRole === 'owner';
  const enabled = guild.pluginSharingEnabled === true;

  const setSharing = useMutation({
    mutationFn: (next: boolean) => api.setGuildPluginSharing(guild.guildId, next),
    onSuccess: async (_res, next) => {
      await qc.invalidateQueries({ queryKey: queryKeys.guilds.detail(guild.guildId) });
      setConfirming(false);
      toast({
        title: next ? 'Plugin sharing enabled' : 'Plugin sharing turned off',
        description: next
          ? 'Members can now share plugin data with this guild.'
          : 'Existing shared plugin spaces are no longer reachable. Turning this back on restores them.',
      });
    },
    onError: (err) => {
      toast({
        title: 'Could not change plugin sharing',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-base flex items-center gap-2">
            <Blocks className="h-4 w-4" aria-hidden="true" />
            Plugin data sharing
          </CardTitle>
          <CardDescription>
            Let apps your members use keep shared data for this guild.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {enabled ? (
          <>
            <p className="text-sm text-muted-foreground">
              Enabled — members can share an app&rsquo;s data with this guild. Every member can view
              it; moderators and admins (and custom roles granted &ldquo;Manage shared data&rdquo;)
              can add and edit. Each app keeps its own separate space.
            </p>
            {isOwner && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={setSharing.isPending}
                onClick={() => setSharing.mutate(false)}
              >
                {setSharing.isPending ? 'Turning off…' : 'Turn off plugin sharing'}
              </Button>
            )}
          </>
        ) : isOwner ? (
          <>
            <p className="text-sm text-muted-foreground">
              Not enabled. Turning this on lets apps your members use see this guild&rsquo;s name
              and create a shared space for it
              {guild.visibility === 'public'
                ? ' — and anyone who joins this public guild gets access to what is in it'
                : ''}
              . You can turn it off again at any time.
            </p>
            <Button type="button" size="sm" onClick={() => setConfirming(true)}>
              Enable plugin sharing
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not enabled. Only the guild owner can turn on plugin data sharing.
          </p>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Allow apps to share data with “{guild.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Apps your members use — including ones built by other people — will be able to see
              that this guild exists and offer it as a place to share their data. A member can then
              create a shared space that every member can read, and that moderators and admins can
              write to.
              {guild.visibility === 'public' && (
                <> This guild is public: anyone who joins can read what is in those spaces.</>
              )}{' '}
              Each app&rsquo;s space is separate and stays inside that app. You can turn this off
              again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setSharing.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={setSharing.isPending}
              onClick={(e) => {
                e.preventDefault();
                setSharing.mutate(true);
              }}
            >
              {setSharing.isPending ? 'Enabling…' : 'Enable plugin sharing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
